import type { ApiClient } from '../client';
import { saveOperatorCurrency } from '../../currency';
import type { CustomPackConfig, GameKind } from './customPack';
import { adaptCustomPackConfig } from './customPackConfigAdapter';
import type { AnimationConfig } from '@game-platform/pack-animation';
import { parseOfferAnimation, rememberOfferAnimation } from '../../openingAnimation';

/**
 * Index-action shape returned by the game_platform `ShortSerializer`.
 * Used by both the classic Lobby (grid of packs) and the modern Slider
 * (per-slot pack/pedestal/logo). Each entry carries every image the
 * slider needs to render its own slot — `packLogoImageUrl` and
 * `pedestalImageUrl` come straight from the per-operator overrides.
 */
export interface PoolOfferListItem {
  id: number;
  title: string;
  /** Per-unit price as a string from the backend (e.g. `"25.0"`). */
  price: string;
  status: string;
  isMultiple: boolean;
  packImageUrl: string | null;
  /**
   * Pack art width ÷ height from the backend's image analysis
   * (`pack_image_metadata`), so the slider can lay out its peeks before the
   * art loads. `null` on older deploys or an upload not analyzed yet.
   */
  packImageAspect: number | null;
  packLogoImageUrl: string | null;
  pedestalImageUrl: string | null;
  suitableBadges: TierBadge[];
  /**
   * This offer's own tier label from the backend `badge` field: the
   * operator's badge_name override when set, else the offer's global badge.
   * Preferred over digging the own row out of `suitableBadges` — that list
   * is grouped by the legacy category_id and can be empty or point at
   * foreign offers. `null` when the pack has no badge or on older deploys.
   */
  badgeName: string | null;
  /**
   * Operator-side currency snapshot from the platform session
   * (`game_platform_operator.currency`, e.g. `"EUR"`). Iframe renders this
   * alongside the USD `price` so the player sees the price in their
   * platform's currency. `null` when the backend omits the field (older
   * deploys) — caller should fall back to USD-only display.
   */
  operatorCurrency: string | null;
  /**
   * USD-per-1-operator-currency-unit rate snapshot from the operator
   * (`game_platform_operator.exchange_rate`). E.g. 1.08 means 1 EUR = 1.08 USD.
   * Combined with `price` (USD) this lets the iframe show the equivalent
   * platform-currency amount: `op_amount = price / exchange_rate`.
   * `null` when the backend omits the field.
   */
  exchangeRate: number | null;
  /**
   * Per-(operator, pack) override — `true` when this operator has opted
   * this pack into the free, no-registration demo rip. `false` on older
   * deploys / unconfigured pairs (the backend default). Gates the
   * "Try a free demo rip" entry point; never affects purchase/odds.
   */
  demoMode: boolean;
}

interface RawIndexOffer {
  id: number | string;
  title?: string;
  name?: string;
  price?: string | number;
  status?: string;
  is_multiple?: boolean;
  pack_image?: RawImage | string | null;
  pack_image_url?: string | null;
  pack_image_metadata?: RawImageMetadata | null;
  pack_logo_image_url?: string | null;
  pedestal_image_url?: string | null;
  suitable_badges?: RawBadge[];
  badge?: { name?: string | null } | null;
  operator_currency?: string | null;
  exchange_rate?: number | string | null;
  demo_mode?: boolean;
}

/**
 * Row shape for the "Repackz" tab of My Packs — one entry per pool_offer
 * the user still has unopened packs of, surfaced by the `unopened`
 * endpoint. `firstUnopenedOrderId` powers the "Open Now" button.
 */
export interface IframeUnopenedOffer {
  id: number;
  title: string;
  price: string;
  packImageUrl: string | null;
  unopenedCount: number;
  firstUnopenedOrderId: string | null;
}

interface RawUnopenedOffer {
  id?: number | string;
  title?: string;
  price?: number | string | null;
  pack_image_url?: string | null;
  unopened_count?: number | string;
  first_unopened_order_id?: number | string | null;
}

export interface UnopenedPoolOffersParams {
  page?: number;
  per?: number;
}

export interface IframeUnopenedOffersPage {
  offers: IframeUnopenedOffer[];
  totalCount: number;
}

interface RawUnopenedMeta {
  total_count?: number | string;
  page?: number | string;
  per?: number | string;
}

export async function getUnopenedPoolOffers(
  client: ApiClient,
  params: UnopenedPoolOffersParams = {},
): Promise<IframeUnopenedOffersPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await client.request<{
    pool_offers: RawUnopenedOffer[];
    meta?: RawUnopenedMeta;
  }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/unopened${suffix}`,
    { method: 'GET' },
  );
  const offers = (raw.pool_offers ?? []).map((o) => ({
    id: Number(o.id ?? 0),
    title: o.title ?? '',
    price: o.price !== undefined && o.price !== null ? String(o.price) : '0',
    packImageUrl: (o.pack_image_url as string) || null,
    unopenedCount: Number(o.unopened_count ?? 0),
    firstUnopenedOrderId:
      o.first_unopened_order_id !== undefined && o.first_unopened_order_id !== null
        ? String(o.first_unopened_order_id)
        : null,
  }));
  // Backend doesn't paginate this endpoint yet — when `meta` is absent we
  // fall back to `offers.length`, so a one-shot full response gets
  // `reachedEnd: true` on the first page (next page would be empty).
  return {
    offers,
    totalCount: Number(raw.meta?.total_count ?? offers.length),
  };
}

export interface ListPoolOffersParams {
  /** `Collectibles::Category` (sport) record id — filters to that category. */
  categoryId?: number;
  /** Defaults to `'Collectibles::Category'` when `categoryId` is set. */
  categoryClassName?: string;
  isMultiple?: boolean;
  orderBy?: 'price' | 'sales_count';
  orderDirection?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  /**
   * Leaves out every row's `suitable_badges` (a query per row on the
   * backend) for callers that name tiers off the flat `badge` instead.
   */
  skipSuitableBadges?: boolean;
}

function mapIndexOffer(o: RawIndexOffer): PoolOfferListItem {
  const fallback: RawDetail = {
    id: o.id,
    pack_image: o.pack_image ?? null,
    pack_image_url: o.pack_image_url ?? null,
  };
  return {
    id: Number(o.id ?? 0),
    title: o.title ?? o.name ?? '',
    price: o.price !== undefined && o.price !== null ? String(o.price) : '0',
    status: typeof o.status === 'string' && o.status ? o.status : 'available',
    isMultiple: o.is_multiple === true,
    packImageUrl: extractImageUrl(fallback),
    packImageAspect: extractImageAspect(o.pack_image_metadata),
    packLogoImageUrl:
      typeof o.pack_logo_image_url === 'string' && o.pack_logo_image_url
        ? o.pack_logo_image_url
        : null,
    pedestalImageUrl:
      typeof o.pedestal_image_url === 'string' && o.pedestal_image_url
        ? o.pedestal_image_url
        : null,
    suitableBadges: mapBadges(o.suitable_badges),
    badgeName:
      typeof o.badge?.name === 'string' && o.badge.name.trim()
        ? o.badge.name.trim()
        : null,
    operatorCurrency: extractOperatorCurrency(o.operator_currency),
    exchangeRate: extractExchangeRate(o.exchange_rate),
    demoMode: o.demo_mode === true,
  };
}

/**
 * Backend ships `operator_currency` as a string (e.g. `"EUR"`) or `null`
 * on older deploys. Empty strings collapse to `null` so the iframe
 * doesn't render a "0" badge for missing config.
 */
function extractOperatorCurrency(
  v: string | null | undefined,
): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Backend ships `exchange_rate` as a JSON number (it Decimal-to-floats it
 * server-side). We accept string too so admin/test fixtures that stringify
 * the decimal still parse. Non-positive or NaN rates collapse to `null`
 * — a zero rate would blow up the `price / rate` op-currency conversion.
 */
function extractExchangeRate(
  v: number | string | null | undefined,
): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Backend ships `buyback_percent` as a 0..1 fraction (like `exchange_rate`).
 * We render it as a whole percent in "{buybackPercent}% FMV Buyback available",
 * so convert via integer math (×1000 / 10) to dodge float drift
 * (0.9 → 90, 1 → 100, 0.855 → 85.5). Missing / non-positive falls back to 90.
 */
function extractBuybackPercent(raw: number | string | null | undefined): number {
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return 90;
  return Math.round(n * 1000) / 10;
}

/**
 * `GET /loot_machine/api/game_platform/iframe/v1/pool_offers`
 *
 * Game-platform-scoped pack listing — the controller already applies
 * `available_for_operator(operator)`, so disabled packs in the operator
 * config never reach the client. Pass `categoryId` (+ `categoryClassName`)
 * to narrow to a single sport / collection, which is what the modern
 * Slider does once the user picks a category in the header.
 */
export async function listPoolOffers(
  client: ApiClient,
  params: ListPoolOffersParams = {},
): Promise<PoolOfferListItem[]> {
  const qs = new URLSearchParams();
  if (params.categoryId !== undefined) {
    qs.set('category_id', String(params.categoryId));
    qs.set('category_class_name', params.categoryClassName ?? 'Collectibles::Category');
  }
  if (params.isMultiple !== undefined) qs.set('is_multiple', String(params.isMultiple));
  if (params.orderBy) qs.set('order_by', params.orderBy);
  if (params.orderDirection) qs.set('order_direction', params.orderDirection);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.perPage !== undefined) qs.set('per_page', String(params.perPage));
  if (params.skipSuitableBadges) qs.set('skip_suitable_badges', 'true');
  const tail = qs.toString();
  const path = `/loot_machine/api/game_platform/iframe/v1/pool_offers${tail ? '?' + tail : ''}`;
  const raw = await client.request<{ pool_offers: RawIndexOffer[] }>(path, { method: 'GET' });
  const items = (raw.pool_offers ?? []).map(mapIndexOffer);
  const firstCurrency = items.find((o) => o.operatorCurrency)?.operatorCurrency;
  saveOperatorCurrency(firstCurrency);
  return items;
}

/**
 * One entry of the tier-selector (`suitable_badges` on the backend).
 * `isSelf: true` → this tier's pool_offer_id matches the currently-viewed
 * offer, i.e. it should render as the active card.
 */
export interface TierBadge {
  poolOfferId: number;
  name: string;
  price: string;
  imageUrl: string | null;
  subImageUrl: string | null;
  isSelf: boolean;
}

/**
 * Pack-detail shape — expanded as follow-up components need more fields
 * (price, odds, rarities, videos, …).
 *
 * The shared ShowSerializer also returns `available_categories` inline, but
 * the backend guards it to `[]` in `Rails.env.development?` — we rely on the
 * separate `listAvailableCategories` endpoint instead, which works in every
 * environment.
 */
export interface PoolOfferDetail {
  id: number;
  title: string;
  /** Per-unit price as a string from the backend (e.g. `"25.0"`). */
  price: string;
  /**
   * Operator-side currency snapshot from the platform session
   * (`game_platform_operator.currency`, e.g. `"EUR"`). See
   * `PoolOfferListItem.operatorCurrency` for usage. `null` when backend
   * omits the field.
   */
  operatorCurrency: string | null;
  /**
   * USD-per-1-operator-currency-unit FX rate from the operator. See
   * `PoolOfferListItem.exchangeRate`. `null` when backend omits the field.
   */
  exchangeRate: number | null;
  /**
   * FMV-buyback percentage (0..100) shown as "{buybackPercent}% FMV Buyback
   * available" on the pack card. Backend ships it as a 0..1 fraction
   * (e.g. `0.9`) like `exchange_rate`; we convert to whole percent. Defaults
   * to 90 when the backend omits or zeroes the field.
   */
  buybackPercent: number;
  packImageUrl: string | null;
  /** Pack art width ÷ height, as on `PoolOfferListItem`. */
  packImageAspect: number | null;
  /**
   * Per-operator override surfaced by the game_platform/iframe show
   * serializer. Null when the operator has no config row or no upload —
   * the iframe falls back to bundled defaults.
   */
  packLogoImageUrl: string | null;
  /** Per-operator override of the pedestal artwork. Null when not set. */
  pedestalImageUrl: string | null;
  /**
   * Backend `pool_offer.status` — `'available'` means the pack is purchasable.
   * Anything else (e.g. `'restoring'`, `'sold_out'`, …) puts the page into a
   * read-only state with a status badge and the bottom action bar hidden.
   */
  status: string;
  /** Tier variants of this pack ("Starter / Premier / Pro" in Figma). */
  suitableBadges: TierBadge[];
  /**
   * Per-operator pack description from the game_platform/iframe show
   * serializer (operator override → PoolOffer default). `null` when the
   * backend omits it — the UI falls back to its built-in copy.
   */
  description: string | null;
  /**
   * URL to the hero-animation Lottie JSON (waves + decorative sport
   * elements). Backend integration pending — currently always null; the
   * iframe falls back to a bundled default. Reserved now so the shape stays
   * stable when the backend adds the field.
   */
  heroAnimationUrl: string | null;
  /** See `PoolOfferListItem.demoMode`. */
  demoMode: boolean;
  /**
   * The pack's primary `LootMachine::PoolCategory` id — the same id as
   * `AvailableCategory.categoryId`. `null` when the pack has no category or
   * the backend predates the field.
   */
  categoryId: number | null;
  /**
   * `'custom_pack'` for the build-your-own pack. Every other pack — and a
   * backend that predates the field — is `'loot_pack'`.
   */
  gameKind: GameKind;
  /** Rules of a custom pack (RTP and selection limits). `null` on every other pack. */
  customPack: CustomPackConfig | null;
  /** This pack's own opening mechanic, replacing the operator's global one
   *  whole. Null: the pack plays the global one. */
  animation: AnimationConfig | null;
}

interface RawImage {
  url?: string | null;
}

interface RawBadge {
  pool_offer_id?: number | string;
  name?: string;
  price?: string | number;
  image?: RawImage | string | null;
  sub_image_url?: string | null;
  is_self?: boolean;
}

interface RawDetail {
  id: number | string;
  title?: string;
  price?: string | number;
  status?: string | null;
  pack_image?: RawImage | string | null;
  pack_image_url?: string | null;
  pack_image_metadata?: RawImageMetadata | null;
  pack_logo_image_url?: string | null;
  pedestal_image_url?: string | null;
  hero_animation_url?: string | null;
  description?: string | null;
  suitable_badges?: RawBadge[];
  operator_currency?: string | null;
  exchange_rate?: number | string | null;
  buyback_percent?: number | string | null;
  demo_mode?: boolean;
  category_id?: number | string | null;
  game_kind?: string | null;
  custom_pack?: unknown;
  animation?: unknown;
}

function extractBadgeImage(b: RawBadge): string | null {
  const i = b.image;
  if (!i) return null;
  if (typeof i === 'string') return i;
  return i.url ?? null;
}

function mapBadges(raw: RawBadge[] | undefined): TierBadge[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((b) => ({
    poolOfferId: Number(b.pool_offer_id ?? 0),
    name: b.name ?? '',
    price: b.price !== undefined && b.price !== null ? String(b.price) : '',
    imageUrl: extractBadgeImage(b),
    subImageUrl: typeof b.sub_image_url === 'string' && b.sub_image_url ? b.sub_image_url : null,
    isSelf: b.is_self === true,
  }));
}

/** ActiveStorage analyzer output for an image (`identified`, `analyzed`, …). */
interface RawImageMetadata {
  width?: number | string | null;
  height?: number | string | null;
}

/** Width ÷ height of an analyzed image; `null` without a usable size. */
function extractImageAspect(meta: RawImageMetadata | null | undefined): number | null {
  const width = Number(meta?.width);
  const height = Number(meta?.height);
  return width > 0 && height > 0 ? width / height : null;
}

function extractImageUrl(p: RawDetail): string | null {
  if (typeof p.pack_image_url === 'string' && p.pack_image_url) {
    return p.pack_image_url;
  }
  if (p.pack_image && typeof p.pack_image === 'object' && p.pack_image.url) {
    return p.pack_image.url;
  }
  return null;
}

/**
 * OddsPanel payload — iframe-scoped endpoint returning structured odds
 * (`tier_name` + `range` + `percentage`) plus `big_win_chance`,
 * `estimated_value` and the value range (`min_value` / `max_value`). Lives
 * under the game_platform/iframe namespace and is intentionally separate
 * from the shared pool_offer route used for hero/badges.
 */
export type VolatilityLevel = 'normal' | 'high' | 'max';

export const VOLATILITY_LEVELS: VolatilityLevel[] = ['normal', 'high', 'max'];

export const MOD_LABELS: Record<VolatilityLevel, string> = {
  normal: 'Normal',
  high: 'High',
  max: 'Max',
};

export interface IframePoolOfferOddsLevel {
  tiers: { tierName: string; range: string; percentage: number }[];
  minValue: number;
  maxValue: number;
  bigWinChance: number;
}

export interface IframePoolOfferOdds {
  id: number;
  title: string;
  /** Expected value — drives the classic Pack's `OddsView` badge. */
  estimatedValue: number;
  bigWinChance: number;
  /**
   * Floor of the cheapest tier — minimum USD value a user could pull
   * from this pack. Drives the modern OddsPanel's "Min Value" stat.
   * Backend computes it via `range_lower_bound(sorted_tiers.first)`;
   * `0` when the pool offer has no tier breakdown.
   */
  minValue: number;
  /**
   * Floor of the priciest tier — drives the modern OddsPanel's "Max Pull"
   * stat. Backend `OddsSummary#max_value` (lower bound of the top bucket,
   * e.g. 250 for `250..Infinity`); falls back to the pack price when the
   * offer has no tier breakdown (see getIframePoolOfferOdds).
   */
  maxValue: number;
  maxForPurchaseCount: number;
  tiers: { tierName: string; range: string; percentage: number }[];
  /**
   * Per-operator override of the Pack-page background image. Resolved
   * server-side from `OperatorPoolOfferAsset#bg_image` for the current
   * `@platform_session.game_platform_operator`. When null, the iframe
   * falls back to the design_system gradient.
   */
  bgImageUrl: string | null;
  levels: Record<VolatilityLevel, IframePoolOfferOddsLevel>;
}

interface RawOddsTier {
  tier_name?: string;
  range?: string;
  percentage?: number | string;
}

interface RawOddsLevel {
  tiers?: RawOddsTier[];
  min_value?: number | string | null;
  max_value?: number | string | null;
  big_win_chance?: number | string;
}

interface RawIframeOffer {
  id?: number | string;
  title?: string;
  price?: number | string | null;
  estimated_value?: number | string | null;
  big_win_chance?: number | string;
  min_value?: number | string | null;
  max_value?: number | string | null;
  max_for_purchase_count?: number | string;
  odds?: RawOddsTier[];
  bg_image_url?: string | null;
  odds_levels?: Partial<Record<VolatilityLevel, RawOddsLevel>>;
}

function mapTiers(raw: RawOddsTier[] | undefined) {
  return (raw ?? []).map((t) => ({
    tierName: t.tier_name ?? '',
    range: t.range ?? '',
    percentage: Number(t.percentage ?? 0),
  }));
}

export function adaptOddsLevels(
  rawLevels: Partial<Record<VolatilityLevel, RawOddsLevel>> | undefined,
  normalFallback: IframePoolOfferOddsLevel,
): Record<VolatilityLevel, IframePoolOfferOddsLevel> {
  const out = {} as Record<VolatilityLevel, IframePoolOfferOddsLevel>;
  for (const level of VOLATILITY_LEVELS) {
    const rl = rawLevels?.[level];
    out[level] = rl
      ? {
          tiers: mapTiers(rl.tiers),
          minValue: Number(rl.min_value ?? 0),
          maxValue: Number(rl.max_value ?? 0),
          bigWinChance: Number(rl.big_win_chance ?? 0),
        }
      : normalFallback;
  }
  return out;
}

export function activeOddsLevel(
  odds: IframePoolOfferOdds,
  level: VolatilityLevel,
): IframePoolOfferOddsLevel {
  return odds.levels[level] ?? odds.levels.normal;
}

export async function getIframePoolOfferOdds(
  client: ApiClient,
  id: string | number,
): Promise<IframePoolOfferOdds> {
  const raw = await client.request<{ pool_offer: RawIframeOffer }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  const p = raw.pool_offer ?? ({} as RawIframeOffer);
  const priceRaw = Number(p.price);
  // "Max Pull" = floor of the priciest tier (backend max_value). Fall back to
  // the pack price when the offer has no tier breakdown so the stat never
  // shows $0.00.
  const mvRaw = Number(p.max_value);
  const maxValue = mvRaw > 0 ? mvRaw : priceRaw > 0 ? priceRaw : 0;
  // Expected value (classic Pack's OddsView) — mirrors the web client's
  // `estimated_value || price` fallback so the badge never shows $0.00.
  const evRaw = Number(p.estimated_value);
  const estimatedValue = evRaw > 0 ? evRaw : priceRaw > 0 ? priceRaw : 0;

  const tiers = mapTiers(p.odds);
  const bigWinChance = Number(p.big_win_chance ?? 0);
  const minValue = Number(p.min_value ?? 0);
  const normalLevel: IframePoolOfferOddsLevel = { tiers, minValue, maxValue, bigWinChance };

  return {
    id: Number(p.id ?? 0),
    title: p.title ?? '',
    estimatedValue,
    bigWinChance,
    minValue,
    maxValue,
    maxForPurchaseCount: Math.max(0, Number(p.max_for_purchase_count ?? 0)),
    tiers,
    bgImageUrl: typeof p.bg_image_url === 'string' && p.bg_image_url ? p.bg_image_url : null,
    levels: adaptOddsLevels(p.odds_levels, normalLevel),
  };
}

/**
 * Fetches `/pool_offers/:id` from the game_platform/iframe namespace. This
 * route is authenticated via `X-Platform-Session` (added automatically by
 * `createApiClient` when a session token is present) and serves a
 * per-operator override of pack assets like `pack_logo_image_url` and
 * `pedestal_image_url`.
 */
export async function getPoolOffer(
  client: ApiClient,
  id: string | number,
): Promise<PoolOfferDetail> {
  const raw = await client.request<{ pool_offer: RawDetail }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  const p = raw.pool_offer ?? ({} as RawDetail);
  if (import.meta.env.DEV) {
    console.log(
      `%c[getPoolOffer] /pool_offers/${id}`,
      'color:#22E11C;font-weight:600',
      p,
    );
  }
  const operatorCurrency = extractOperatorCurrency(p.operator_currency);
  saveOperatorCurrency(operatorCurrency);
  const gameKind: GameKind = p.game_kind === 'custom_pack' ? 'custom_pack' : 'loot_pack';
  const animation = parseOfferAnimation(p.animation);
  if ('animation' in p && Number(p.id) > 0) rememberOfferAnimation(Number(p.id), animation);
  return {
    id: Number(p.id),
    title: p.title ?? '',
    price: p.price !== undefined && p.price !== null ? String(p.price) : '0',
    operatorCurrency,
    exchangeRate: extractExchangeRate(p.exchange_rate),
    buybackPercent: extractBuybackPercent(p.buyback_percent),
    packImageUrl: extractImageUrl(p),
    packImageAspect: extractImageAspect(p.pack_image_metadata),
    packLogoImageUrl:
      typeof p.pack_logo_image_url === 'string' && p.pack_logo_image_url
        ? p.pack_logo_image_url
        : null,
    pedestalImageUrl:
      typeof p.pedestal_image_url === 'string' && p.pedestal_image_url
        ? p.pedestal_image_url
        : null,
    status: typeof p.status === 'string' && p.status ? p.status : 'available',
    suitableBadges: mapBadges(p.suitable_badges),
    heroAnimationUrl:
      typeof p.hero_animation_url === 'string' && p.hero_animation_url
        ? p.hero_animation_url
        : null,
    description:
      typeof p.description === 'string' && p.description ? p.description : null,
    demoMode: p.demo_mode === true,
    categoryId: extractCategoryId(p.category_id),
    gameKind,
    customPack: gameKind === 'custom_pack' ? adaptCustomPackConfig(p.custom_pack) : null,
    animation,
  };
}

function extractCategoryId(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}
